import { IconX } from "@tabler/icons-react";
import { cva } from "class-variance-authority";
import type { VariantProps } from "class-variance-authority";
import clsx from "clsx";

type BadgeProps = {
	children?: React.ReactNode;
	className?: string;
	allowClose?: boolean;
	customColorHex?: string;
} & VariantProps<typeof badgeStyles>;

const badgeStyles = cva(
	"inline-flex items-center justify-center whitespace-nowrap w-auto gap-1 leading-tight font-semibold",
	{
		variants: {
			size: {
				default: "rounded-md h-[38px] gap-[2px] px-4 text-base",
				xs: "rounded h-[22px] px-1.5 text-xs",
				sm: "rounded h-[28px] px-2.5 text-sm",
			},
			color: {
				default: "bg-primary-500 text-white",
				"default-light": "bg-primary-100 text-primary-600",
				red: "bg-red-500 text-white",
				"red-light": "bg-red-100 text-red-600",
				green: "bg-green-500 text-white",
				"green-light": "bg-green-100 text-green-600",
				yellow: "bg-yellow-500 text-white",
				"yellow-light": "bg-yellow-100 text-yellow-600",
				blue: "bg-blue-500 text-white",
				"blue-light": "bg-blue-100 text-blue-600",
				gray: "bg-slate-500 text-white",
				"gray-light": "bg-slate-100 text-slate-600",
				custom: "",
			},
		},
		defaultVariants: {
			size: "default",
			color: "default",
		},
	},
);

export default function Badge({
	children,
	className,
	size,
	color,
	allowClose,
	customColorHex,
}: BadgeProps) {
	const isCustomColor = color === "custom";

	return (
		<div
			className={clsx(
				badgeStyles({
					size,
					color: isCustomColor ? undefined : color,
				}),
				className,
			)}
			style={
				isCustomColor && customColorHex
					? { backgroundColor: customColorHex }
					: undefined
			}
		>
			{children}
			{allowClose && (
				<button
					type="button"
					className="font-normal opacity-50 hover:opacity-100"
				>
					<IconX size={14} stroke={2} />
				</button>
			)}
		</div>
	);
}

export const BadgeIndicator = ({ color }: { color?: string }) => (
	<div
		className="flex w-[10px] h-[10px] rounded-full"
		style={{ backgroundColor: color }}
	/>
);

export const BadgeWrapper = ({
	value,
	colorMapping,
}: {
	value: string;
	colorMapping: Record<string, BadgeProps["color"]>;
}) => {
	const color = colorMapping[value] || "gray";
	return (
		<Badge size="xs" color={color}>
			{value}
		</Badge>
	);
};
